import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, DownloadSimple, Archive, ArrowClockwise, Trash, PencilSimple } from "@phosphor-icons/react";
import { downloadCsv } from "@/lib/exports";

const ACCOUNT_TYPES = ["Bank", "PayPal", "Stripe"];
const CURRENCIES = ["EUR", "USD", "GBP", "INR"];

const empty = { account_name: "", country: "", currency: "EUR", owner: "", sub_account: "", bank: "", account_type: "Bank", iban: "", account_number: "", active: true, commission_per_transaction: 0 };

export default function PaymentAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState({ country: "all", account_type: "all", status: "active" });

  const load = async () => {
    const params = {};
    if (filter.country !== "all") params.country = filter.country;
    if (filter.account_type !== "all") params.account_type = filter.account_type;
    if (filter.status === "active") params.active_only = true;
    if (filter.status === "archived") params.include_archived = true;
    const r = await api.get("/payment-accounts", { params });
    let rows = r.data;
    if (filter.status === "inactive") rows = rows.filter((a) => !a.active && !a.archived);
    if (filter.status === "archived") rows = rows.filter((a) => a.archived);
    setAccounts(rows);
  };
  useEffect(() => { load(); }, [filter]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty, ...a }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, commission_per_transaction: parseFloat(form.commission_per_transaction) || 0 };
    try {
      if (editing) await api.put(`/payment-accounts/${editing.account_id}`, payload);
      else await api.post("/payment-accounts", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const toggleActive = async (a, next) => {
    try {
      await api.patch(`/payment-accounts/${a.account_id}/${next ? "activate" : "deactivate"}`);
      toast.success(next ? `${a.account_name} activated` : `${a.account_name} deactivated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle");
    }
  };

  const archive = async (a) => {
    try {
      const usage = (await api.get(`/payment-accounts/${a.account_id}/usage`)).data;
      const msg = `Archive "${a.account_name}"?\n\nThis account has:\n· ${usage.payments_count} payment(s)\n· ${usage.collections_count} collection(s)\n· ${usage.linked_users_count} linked user(s)\n\nArchived accounts are hidden from all lists and cannot receive new payments. Existing records stay intact.`;
      if (!window.confirm(msg)) return;
      await api.delete(`/payment-accounts/${a.account_id}`);
      toast.success("Archived");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive");
    }
  };

  const restore = async (a) => {
    try {
      await api.patch(`/payment-accounts/${a.account_id}/restore`);
      toast.success(`${a.account_name} restored`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore");
    }
  };

  const hardDelete = async (a) => {
    try {
      const usage = (await api.get(`/payment-accounts/${a.account_id}/usage`)).data;
      if (!usage.can_hard_delete) {
        toast.error(`Cannot permanently delete: ${usage.payments_count} payment(s), ${usage.collections_count} collection(s), ${usage.linked_users_count} linked user(s).`);
        return;
      }
      if (!window.confirm(`Permanently DELETE "${a.account_name}"?\n\nThis cannot be undone.`)) return;
      await api.delete(`/payment-accounts/${a.account_id}?hard=true`);
      toast.success("Deleted permanently");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="p-8" data-testid="payment-accounts-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Finance</p>
          <h1 className="text-4xl font-black tracking-tighter">Payment Accounts</h1>
        </div>
        <Button onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="new-account-button">
          <Plus size={16} weight="bold" className="mr-1" /> New Account
        </Button>
      </div>

      <div className="flex gap-3 mb-4">
        <Select value={filter.country} onValueChange={(v) => setFilter({ ...filter, country: v })}>
          <SelectTrigger className="w-[180px] border-zinc-300 rounded-md" data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            <SelectItem value="Spain">Spain</SelectItem>
            <SelectItem value="India">India</SelectItem>
            <SelectItem value="Europe">Europe</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.account_type} onValueChange={(v) => setFilter({ ...filter, account_type: v })}>
          <SelectTrigger className="w-[180px] border-zinc-300 rounded-md" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-[180px] border-zinc-300 rounded-md" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="inactive">Inactive only</SelectItem>
            <SelectItem value="archived">Archived only</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => downloadCsv("/exports/payments", "payments.csv")}
            className="border-zinc-300 rounded-md" data-testid="export-payments-button">
            <DownloadSimple size={14} weight="bold" className="mr-1" /> Payments CSV
          </Button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white" data-testid="accounts-table">
        <table className="w-full data-table">
          <thead><tr>
            <th className="text-left px-4 py-2.5">Account</th>
            <th className="text-left px-4 py-2.5">Owner / Sub</th>
            <th className="text-left px-4 py-2.5">Bank</th>
            <th className="text-left px-4 py-2.5">Type</th>
            <th className="text-left px-4 py-2.5">Country</th>
            <th className="text-left px-4 py-2.5">Currency</th>
            <th className="text-right px-4 py-2.5">Commission</th>
            <th className="text-center px-4 py-2.5">Active</th>
            <th className="text-right px-4 py-2.5">Actions</th>
          </tr></thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.account_id} className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${a.archived ? "opacity-50 bg-zinc-50" : ""}`} data-testid={`account-row-${a.account_id}`}>
                <td className="px-4 py-3 text-sm font-medium">
                  {a.account_name}
                  {a.archived && <span className="ml-2 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-semibold bg-zinc-200 text-zinc-600 rounded">Archived</span>}
                </td>
                <td className="px-4 py-3 text-sm">
                  {a.owner || "—"}
                  {a.sub_account && a.sub_account !== a.owner && (
                    <span className="ml-1 text-zinc-500">/ {a.sub_account}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">{a.bank || "—"}</td>
                <td className="px-4 py-3 text-sm">{a.account_type}</td>
                <td className="px-4 py-3 text-sm">{a.country}</td>
                <td className="px-4 py-3 text-sm font-mono">{a.currency}</td>
                <td className="px-4 py-3 text-sm font-mono text-right">{a.commission_per_transaction}</td>
                <td className="px-4 py-3 text-center">
                  <Switch
                    checked={!!a.active}
                    disabled={!!a.archived}
                    onCheckedChange={(v) => toggleActive(a, v)}
                    data-testid={`toggle-active-${a.account_id}`}
                    aria-label={`Toggle ${a.account_name}`}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => openEdit(a)}
                      title="Edit"
                      className="p-1.5 hover:bg-zinc-200 rounded"
                      data-testid={`edit-account-${a.account_id}`}
                    >
                      <PencilSimple size={14} />
                    </button>
                    {a.archived ? (
                      <>
                        <button
                          onClick={() => restore(a)}
                          title="Restore"
                          className="p-1.5 hover:bg-emerald-50 text-emerald-700 rounded"
                          data-testid={`restore-account-${a.account_id}`}
                        >
                          <ArrowClockwise size={14} />
                        </button>
                        <button
                          onClick={() => hardDelete(a)}
                          title="Delete permanently"
                          className="p-1.5 hover:bg-red-50 text-red-600 rounded"
                          data-testid={`delete-account-${a.account_id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => archive(a)}
                        title="Archive"
                        className="p-1.5 hover:bg-amber-50 text-amber-700 rounded"
                        data-testid={`archive-account-${a.account_id}`}
                      >
                        <Archive size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {accounts.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">No accounts match this filter.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Account" : "New Account"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="account-form">
            <F label="Account Name *"><Input required value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} className="border-zinc-300 rounded-md" data-testid="acc-name-input" /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Country *"><Input required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="border-zinc-300 rounded-md" data-testid="acc-country-input" /></F>
              <F label="Currency">
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="border-zinc-300 rounded-md" data-testid="acc-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Owner"><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="border-zinc-300 rounded-md" data-testid="acc-owner-input" /></F>
              <F label="Sub-account (family member)"><Input value={form.sub_account} onChange={(e) => setForm({ ...form, sub_account: e.target.value })} placeholder="Leave blank if same as owner" className="border-zinc-300 rounded-md" data-testid="acc-sub-input" /></F>
            </div>
            <F label="Bank"><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} className="border-zinc-300 rounded-md" data-testid="acc-bank-input" /></F>
            <F label="Account Type">
              <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                <SelectTrigger className="border-zinc-300 rounded-md" data-testid="acc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="IBAN"><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="border-zinc-300 rounded-md font-mono" data-testid="acc-iban-input" /></F>
              <F label="Account Number"><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="border-zinc-300 rounded-md font-mono" data-testid="acc-number-input" /></F>
            </div>
            <F label="Commission per Transaction"><Input type="number" step="0.01" value={form.commission_per_transaction} onChange={(e) => setForm({ ...form, commission_per_transaction: e.target.value })} className="border-zinc-300 rounded-md" data-testid="acc-commission-input" /></F>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="acc-active-checkbox" />
              Active
            </label>
            <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="save-account-button">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
